import { supabase, getSchema } from '@/lib/supabase';
import {
  mapSupplierCostDocumentViewFromDb,
  type SupplierCostDocumentView,
} from '../types';

/**
 * Get the storage bucket name based on environment
 */
function getBucketName(): string {
  const schema = getSchema();
  return schema === 'testing' ? 'project-documents-testing' : 'project-documents';
}

/**
 * Get supplier cost documents for a specific project
 */
export async function getProjectSupplierCostDocuments(
  projectId: string
): Promise<SupplierCostDocumentView[]> {
  const { data, error } = await supabase
    .from('v_supplier_cost_documents')
    .select('*')
    .eq('project_id', projectId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching project supplier cost documents:', error);
    throw error;
  }

  return (data || []).map(mapSupplierCostDocumentViewFromDb);
}

/**
 * Get all supplier cost documents for a business
 */
export async function getSupplierCostDocuments(
  businessId: string,
  filters?: { projectId?: string; supplierId?: string }
): Promise<SupplierCostDocumentView[]> {
  let query = supabase
    .from('v_supplier_cost_documents')
    .select('*')
    .eq('business_id', businessId)
    .order('created_at', { ascending: false });

  if (filters?.projectId) {
    query = query.eq('project_id', filters.projectId);
  }

  if (filters?.supplierId) {
    query = query.eq('supplier_id', filters.supplierId);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching supplier cost documents:', error);
    throw error;
  }

  return (data || []).map(mapSupplierCostDocumentViewFromDb);
}

/**
 * Get a single supplier cost document by ID
 */
export async function getSupplierCostDocumentById(
  id: string
): Promise<SupplierCostDocumentView | null> {
  const { data, error } = await supabase
    .from('v_supplier_cost_documents')
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null; // Not found
    console.error('Error fetching supplier cost document:', error);
    throw error;
  }

  return data ? mapSupplierCostDocumentViewFromDb(data) : null;
}

/**
 * Update supplier assignment for a document
 */
export async function updateDocumentSupplier(
  documentId: string,
  supplierId: string | null
): Promise<void> {
  const { error } = await supabase
    .from('documents')
    .update({ supplier_id: supplierId })
    .eq('id', documentId);

  if (error) {
    console.error('Error updating document supplier:', error);
    throw error;
  }
}

/**
 * Update project assignment for a document
 */
export async function updateDocumentProject(
  documentId: string,
  projectId: string | null
): Promise<void> {
  const { error } = await supabase
    .from('documents')
    .update({ project_id: projectId })
    .eq('id', documentId);

  if (error) {
    console.error('Error updating document project:', error);
    throw error;
  }
}

/**
 * Update a supplier cost document
 */
export async function updateSupplierCostDocument(params: {
  id: string;
  supplierId?: string | null;
  documentType?: 'receipt' | 'expense_invoice';
}): Promise<void> {
  const { id, supplierId, documentType } = params;

  const updates: Record<string, unknown> = {};

  if (supplierId !== undefined) {
    updates.supplier_id = supplierId;
  }

  if (documentType !== undefined) {
    updates.document_type = documentType;
  }

  if (Object.keys(updates).length === 0) {
    return; // Nothing to update
  }

  const { error } = await supabase
    .from('documents')
    .update(updates)
    .eq('id', id);

  if (error) {
    console.error('Error updating supplier cost document:', error);
    throw error;
  }
}

/**
 * Delete a supplier cost document (removes from storage and database)
 */
export async function deleteSupplierCostDocument(id: string, filePath: string): Promise<void> {
  const bucketName = getBucketName();

  // Delete from storage
  const { error: storageError } = await supabase.storage
    .from(bucketName)
    .remove([filePath]);

  if (storageError) {
    console.error('Error deleting document from storage:', storageError);
    // Continue to delete from database anyway
  }

  // Delete from database
  const { error: dbError } = await supabase
    .from('documents')
    .delete()
    .eq('id', id);

  if (dbError) {
    console.error('Error deleting document from database:', dbError);
    throw dbError;
  }
}

/**
 * Get a signed URL for viewing/downloading a document file
 */
export async function getSupplierCostDocumentUrl(
  filePath: string,
  expiresIn: number = 3600
): Promise<string> {
  const bucketName = getBucketName();

  const { data, error } = await supabase.storage
    .from(bucketName)
    .createSignedUrl(filePath, expiresIn);

  if (error) {
    console.error('Error creating signed URL:', error);
    throw error;
  }

  return data.signedUrl;
}

/**
 * Get signed URLs for multiple files (batch)
 */
export async function getSupplierCostDocumentUrls(
  filePaths: string[],
  expiresIn: number = 3600
): Promise<Map<string, string>> {
  const bucketName = getBucketName();
  const urlMap = new Map<string, string>();

  const results = await Promise.allSettled(
    filePaths.map(async (filePath) => {
      const { data, error } = await supabase.storage
        .from(bucketName)
        .createSignedUrl(filePath, expiresIn);

      if (error) throw error;
      return { filePath, url: data.signedUrl };
    })
  );

  for (const result of results) {
    if (result.status === 'fulfilled') {
      urlMap.set(result.value.filePath, result.value.url);
    }
  }

  return urlMap;
}
